'use client'

import { useCallback, useEffect, useRef } from 'react'

interface BatchedStreamOptions<T> {
  intervalMs?: number
  hiddenIntervalMs?: number
  maxPendingItems?: number
  getItemKey?: (item: T) => string
  onCoalescedItems?: (count: number) => void
  onDroppedItems?: (count: number) => void
  resetKey?: string
}

export function appendToKeyedStreamQueue<T>(
  pending: Map<string, T>,
  items: readonly T[],
  getItemKey: (item: T) => string,
  maxPendingItems?: number
): { coalesced: number; dropped: number } {
  let coalesced = 0
  for (const item of items) {
    const key = getItemKey(item)
    if (pending.delete(key)) coalesced += 1
    pending.set(key, item)
  }

  let dropped = 0
  if (maxPendingItems !== undefined) {
    while (pending.size > maxPendingItems) {
      const oldestKey = pending.keys().next().value
      if (oldestKey === undefined) break
      pending.delete(oldestKey)
      dropped += 1
    }
  }
  return { coalesced, dropped }
}

/**
 * Appends a stream batch while optionally keeping only the newest pending items.
 * The default remains lossless; callers must opt into a finite ceiling only when
 * their UI has latest-window semantics (for example, a capped news feed).
 */
export function appendToBoundedStreamQueue<T>(
  pending: T[],
  items: readonly T[],
  maxPendingItems?: number
): number {
  if (items.length === 0) return 0

  for (const item of items) pending.push(item)
  if (maxPendingItems === undefined || pending.length <= maxPendingItems) return 0

  const dropped = pending.length - maxPendingItems
  pending.splice(0, dropped)
  return dropped
}

/**
 * Buffers stream events outside React and publishes them as one batch.
 * It is lossless by default; a caller with latest-window semantics may opt into
 * `maxPendingItems` and observe drops through `onDroppedItems`.
 */
export function useBatchedStreamCallback<T>(
  onFlush: (items: readonly T[]) => void,
  {
    intervalMs = 100,
    hiddenIntervalMs = 1_000,
    maxPendingItems,
    getItemKey,
    onCoalescedItems,
    onDroppedItems,
    resetKey = 'default',
  }: BatchedStreamOptions<T> = {}
) {
  const pendingRef = useRef<T[]>([])
  const keyedPendingRef = useRef(new Map<string, T>())
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onFlushRef = useRef(onFlush)

  useEffect(() => {
    onFlushRef.current = onFlush
  }, [onFlush])

  const cancel = useCallback(() => {
    if (timerRef.current !== null) clearTimeout(timerRef.current)
    timerRef.current = null
    pendingRef.current = []
    keyedPendingRef.current.clear()
  }, [])

  const flush = useCallback(() => {
    if (timerRef.current !== null) clearTimeout(timerRef.current)
    timerRef.current = null
    const pending = getItemKey ? Array.from(keyedPendingRef.current.values()) : pendingRef.current
    if (pending.length === 0) return

    pendingRef.current = []
    keyedPendingRef.current.clear()
    onFlushRef.current(pending)
  }, [getItemKey])

  const enqueue = useCallback(
    (items: readonly T[]) => {
      if (items.length === 0) return
      let dropped: number
      if (getItemKey) {
        const result = appendToKeyedStreamQueue(
          keyedPendingRef.current,
          items,
          getItemKey,
          maxPendingItems
        )
        dropped = result.dropped
        if (result.coalesced > 0) onCoalescedItems?.(result.coalesced)
      } else {
        dropped = appendToBoundedStreamQueue(pendingRef.current, items, maxPendingItems)
      }
      if (dropped > 0) onDroppedItems?.(dropped)
      if (timerRef.current !== null) return

      const delay =
        typeof document !== 'undefined' && document.visibilityState === 'hidden'
          ? hiddenIntervalMs
          : intervalMs
      timerRef.current = setTimeout(flush, delay)
    },
    [
      flush,
      getItemKey,
      hiddenIntervalMs,
      intervalMs,
      maxPendingItems,
      onCoalescedItems,
      onDroppedItems,
    ]
  )

  useEffect(() => cancel, [cancel, resetKey])

  return { cancel, enqueue, flush }
}
